/**
 * ============================================================
 * KEJJA OS — Finance → Income
 * Multiple income sources per home, attributed to earners.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Briefcase, Gift, TrendingUp, Trash2, Pencil, Users, X, Loader2, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { useSettingsContext } from '@/pages/settings/SettingsLayout';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface IncomeSource {
  id: string; homeId: string;
  earnerUserId: string | null; earnerName: string | null; earnerDisplay: string;
  sourceType: string; name: string;
  amountMinor: number; currencyCode: string; frequency: string;
  notes: string | null; isActive: boolean;
  monthlyEquivalent: number; actualLast30d: number;
}

interface Summary {
  monthlyExpected: number; actualLast30d: number;
  byEarner: Array<{ earnerName: string; earnerUserId: string | null; monthly: number; sources: number }>;
  byType: Array<{ type: string; monthly: number }>;
}

const TYPE_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  salary:     { label: 'Salary',     icon: Briefcase,  color: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  freelance:  { label: 'Freelance',  icon: TrendingUp, color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  business:   { label: 'Business',   icon: TrendingUp, color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  gift:       { label: 'Gift',       icon: Gift,       color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400' },
  investment: { label: 'Investment', icon: TrendingUp, color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  pension:    { label: 'Pension',    icon: Wallet,     color: 'bg-slate-500/10 text-slate-600 dark:text-slate-400' },
  rental:     { label: 'Rental',     icon: Briefcase,  color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  other:      { label: 'Other',      icon: Wallet,     color: 'bg-neutral-500/10 text-neutral-600 dark:text-neutral-400' },
};

const FREQ_LABEL: Record<string, string> = {
  weekly: '/week', biweekly: '/2 weeks', monthly: '/month',
  quarterly: '/quarter', yearly: '/year', irregular: '· irregular',
};

function fmtKES(minor: number): string {
  return 'KES ' + (minor / 100).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function IncomePage() {
  const { homeId } = useSettingsContext();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<IncomeSource | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: sourcesData, isLoading } = useQuery({
    queryKey: ['income', homeId, 'sources'],
    queryFn: () => api.get<{ sources: IncomeSource[] }>(`/homes/${homeId}/income/sources`),
  });
  const { data: summary } = useQuery({
    queryKey: ['income', homeId, 'summary'],
    queryFn: () => api.get<Summary>(`/homes/${homeId}/income/summary`),
  });
  const { data: membersData } = useQuery({
    queryKey: ['home', homeId, 'members'],
    queryFn: () => api.get<{ members: Array<{ userId: string; displayName: string; displayNickname: string | null; email: string; role: string }> }>(`/homes/${homeId}/members`),
  });
  const members = membersData?.members ?? [];

  const sources = sourcesData?.sources ?? [];

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/homes/${homeId}/income/sources/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['income', homeId] }); },
  });

  const remove = (id: string, name: string) => {
    if (!confirm(`Remove "${name}" as an income source?`)) return;
    delMut.mutate(id);
  };

  return (
    <div className="pb-16 space-y-5">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Monthly expected</div>
            <div className="text-3xl font-semibold">{fmtKES(summary.monthlyExpected)}</div>
            <div className="text-xs text-muted-foreground mt-1">{sources.filter(s => s.isActive).length} active source{sources.filter(s => s.isActive).length !== 1 ? 's' : ''}</div>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Actual · last 30 days</div>
            <div className="text-3xl font-semibold text-emerald-600 dark:text-emerald-400">{fmtKES(summary.actualLast30d)}</div>
            <div className="text-xs text-muted-foreground mt-1">from linked transactions</div>
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Earners</div>
            <div className="text-3xl font-semibold">{summary.byEarner.length}</div>
            <div className="text-xs text-muted-foreground mt-1 truncate">{summary.byEarner.map(e => e.earnerName).join(' · ') || '—'}</div>
          </CardContent></Card>
        </div>
      )}

      {/* By earner breakdown */}
      {summary && summary.byEarner.length > 0 && (
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold mb-4"><Users className="w-4 h-4" /> Income by earner</div>
          <div className="space-y-3">
            {summary.byEarner.map((e, i) => {
              const pct = summary.monthlyExpected ? Math.round((e.monthly / summary.monthlyExpected) * 100) : 0;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <div><span className="font-medium">{e.earnerName}</span> <span className="text-xs text-muted-foreground">· {e.sources} source{e.sources !== 1 ? 's' : ''}</span></div>
                    <div className="font-mono tabular-nums"><span className="font-semibold">{fmtKES(e.monthly)}</span> <span className="text-muted-foreground text-xs">· {pct}%</span></div>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent></Card>
      )}

      {/* Sources list */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Income sources</h2>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-1" /> Add income</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : sources.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <Wallet className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
          <div className="text-sm font-medium mb-1">No income sources yet</div>
          <div className="text-xs text-muted-foreground mb-5">Add the salaries, gigs, gifts, or other income that come into your home.</div>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }}><Plus className="w-4 h-4 mr-1" /> Add first source</Button>
        </CardContent></Card>
      ) : (
        <div className="grid gap-2">
          {sources.map(s => {
            const meta = TYPE_META[s.sourceType] ?? TYPE_META.other!;
            const Icon = meta.icon;
            return (
              <Card key={s.id} className={cn(!s.isActive && 'opacity-50')}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', meta.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate">{s.name}</div>
                      {!s.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">inactive</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {s.earnerDisplay} · {meta.label} <span className="text-muted-foreground/60">· last 30d {fmtKES(s.actualLast30d)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono tabular-nums text-sm font-semibold">{fmtKES(s.amountMinor)}</div>
                    <div className="text-[10px] text-muted-foreground">{FREQ_LABEL[s.frequency] ?? s.frequency}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(s); setShowForm(true); }} className="w-8 h-8 rounded hover:bg-muted flex items-center justify-center" aria-label="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(s.id, s.name)} disabled={delMut.isPending} className="w-8 h-8 rounded hover:bg-muted text-rose-600 flex items-center justify-center" aria-label="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showForm && (
        <IncomeFormDialog
          homeId={homeId}
          editing={editing}
          members={members.map((m) => ({ id: m.userId, name: m.displayNickname || m.displayName, email: m.email, role: m.role }))}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['income', homeId] }); setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function IncomeFormDialog({ homeId, editing, members, onClose, onSaved }: {
  homeId: string;
  editing: IncomeSource | null;
  members: Array<{ id: string; name: string; email: string; role: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [earnerUserId, setEarnerUserId] = useState(editing?.earnerUserId ?? '');
  const [earnerName, setEarnerName] = useState(editing?.earnerName ?? '');
  const [sourceType, setSourceType] = useState(editing?.sourceType ?? 'salary');
  const [frequency, setFrequency] = useState(editing?.frequency ?? 'monthly');
  const [amountKes, setAmountKes] = useState(editing ? (editing.amountMinor / 100).toString() : '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const mut = useMutation({
    mutationFn: async () => {
      const body = {
        earnerUserId: earnerUserId || null,
        earnerName: earnerUserId ? null : (earnerName || null),
        sourceType, name,
        amountMinor: Math.round(Number(amountKes) * 100),
        frequency, notes: notes || null,
        ...(editing ? { isActive } : {}),
      };
      if (editing) return api.patch(`/homes/${homeId}/income/sources/${editing.id}`, body);
      return api.post(`/homes/${homeId}/income/sources`, body);
    },
    onSuccess: onSaved,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !amountKes) return;
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-background w-full max-w-md rounded-t-xl md:rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">{editing ? 'Edit income source' : 'Add income source'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded hover:bg-muted flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Safaricom salary · Uber driving" required className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Earner</Label>
            <select value={earnerUserId} onChange={(e) => setEarnerUserId(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-md border bg-background text-sm">
              <option value="">— Custom (non-member) —</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name} · {m.role}</option>)}
            </select>
            {!earnerUserId && (
              <Input value={earnerName} onChange={(e) => setEarnerName(e.target.value)} placeholder="Name of the earner (e.g. Mama, grandfather)" className="mt-2" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-md border bg-background text-sm">
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Frequency</Label>
              <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-md border bg-background text-sm">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="irregular">Irregular</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Amount (KES)</Label>
            <Input type="number" step="100" value={amountKes} onChange={(e) => setAmountKes(e.target.value)} placeholder="85000" required className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. received end of month" className="mt-1" />
          </div>
          {editing && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
          )}
        </form>
        <div className="p-4 border-t flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" onClick={submit} disabled={mut.isPending} className="flex-1">
            {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}

